import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: ${props => props.theme.background};
`;

const LoginBox = styled.div`
  background-color: ${props => props.theme.surface};
  padding: 30px;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 20px;
  color: ${props => props.theme.primary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  box-sizing: border-box;
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 10px;
  padding-right: 40px;
  margin-bottom: 15px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  box-sizing: border-box;
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 10px;
  top: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary || '#888'};
  padding: 0;
  font-size: 14px;
  
  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 10px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.errorText};
  margin-bottom: 15px;
  text-align: center;
`;

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Check if already logged in
  useEffect(() => {
    const loggedIn = localStorage.getItem('adminLoggedIn');
    const adminToken = localStorage.getItem('adminToken');
    if (loggedIn === 'true' && adminToken) {
      navigate('/ops-k7x2q9');
    } else if (loggedIn === 'true' && !adminToken) {
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminUsername');
    }
  }, [navigate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Use relative path for API base to avoid mixed content
      const apiBase = '/api';
      
      try {
        // First try to hit the test endpoint to see if admin routes are working
        const testResponse = await axios.get(`${apiBase}/admin/test`);
      } catch (testError) {
        console.warn('Admin routes test endpoint not available:', testError);
      }
      
      
      const response = await axios.post(`${apiBase}/admin/login`, {
        username: username,
        password: password
      });
      
      
      if (response.data.success) {
        // Store auth token and session flags
        localStorage.setItem('adminLoggedIn', 'true');
        localStorage.setItem('adminUsername', response.data.username);
        localStorage.setItem('adminToken', response.data.token);
        navigate('/ops-k7x2q9');
      } else {
        setError(response.data.message || 'Login failed');
      }
    } catch (err: any) {
      console.error('Login error details:', err);
      if (err.response?.status === 404) {
        setError('Login service is not available (404). Please check if the backend server is running.');
      } else if (err.code === 'ERR_NETWORK') {
        setError('Network error. Backend may not be running or could be on a different port.');
      } else {
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <LoginBox>
        <Title>Admin Login</Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Form onSubmit={handleSubmit}>
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          
          <PasswordWrapper>
            <PasswordInput
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <ToggleButton 
              type="button" 
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? '🙈' : '👁️'}
            </ToggleButton>
          </PasswordWrapper>
          
          <Button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </Form>
        
        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '14px', color: '#888' }}>
          <p>Use admin credentials provided by your administrator</p>
        </div>
      </LoginBox>
    </Container>
  );
};

export default AdminLogin;
